import { SearchResultsResponse } from '@/lib/types/search';
import { defaultStoreNumber } from '@/lib/constants/store';
import { normalizeSearchQuery } from '@/lib/functions/search/normalizeSearchQuery';
import { SEARCH_KEY_MAPPING } from '@/lib/config/search/searchKeyMapping';
import { fetchSyliusSearch } from '@/lib/functions/search/fetchSyliusSearch';

import { PriceRangeValues } from '@/components/search/searchFilters';

type SearchParams = {
  userQuery: string;
  force?: boolean;
  searchKey?: string;
  facets: Record<string, string | PriceRangeValues>;
  page: number;
  pageSize: number;
  orderBy?: string;
  storeNumber?: number;
};

export async function getSearchResults({
  userQuery,
  force = false,
  searchKey,
  facets: userFacets,
  page,
  pageSize,
  orderBy,
  storeNumber = defaultStoreNumber,
}: SearchParams): Promise<SearchResultsResponse> {
  try {
    const { corrected: normalizedUserQuery } = normalizeSearchQuery(userQuery);

    // Normalize searchKey as a path
    const normalizedKey = searchKey?.replace(/^\/|\/$/g, '');
    const config = normalizedKey ? SEARCH_KEY_MAPPING[normalizedKey] : undefined;

    const mergedFacets: Record<string, string | string[]> = {};
    let priceRange: PriceRangeValues | undefined;

    // Handle incoming user facets
    for (const [key, raw] of Object.entries(userFacets)) {
      if (key === 'price_range' && typeof raw === 'object' && raw !== null) {
        priceRange = raw as PriceRangeValues;
        continue;
      }

      if (typeof raw === 'string') {
        const values = raw.split(',').map(v => v.trim()).filter(Boolean);
        if (values.length) {
          if (key !== 'price_range') {
            mergedFacets[key] = values.join(',');
            if (values.length > 1) {
              mergedFacets[`${key}_mode`] = 'must_one_of';
            }
          }
        }
      }
    }

    // Merge config-defined facets (do not override price_range)
    if (config?.facets) {
      for (const [key, values] of Object.entries(config.facets)) {
        if (key === 'price_range') continue;

        const existingRaw = mergedFacets[key];
        const existing = typeof existingRaw === 'string' ? existingRaw.split(',') : [];
        const merged = Array.from(new Set([...existing, ...values]));
        mergedFacets[key] = JSON.stringify(merged);
        if (merged.length > 1) {
          mergedFacets[`${key}_mode`] = 'must_one_of';
        }
      }
    }

    const baseQuery = config?.type === 'query' && config.query ? config.query : undefined;

    const isServer = typeof window === 'undefined';

    if (isServer) {
      return await fetchSyliusSearch({
        baseQuery,
        userQuery: force ? userQuery : normalizedUserQuery,
        orderBy,
        storeNumber: storeNumber.toString(),
        page: page.toString(),
        pageSize: pageSize.toString(),
        facets: mergedFacets,
        priceRange,
      });
    } else {
      const response = await fetch('/api/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baseQuery,
          userQuery: force ? userQuery : normalizedUserQuery,
          orderBy,
          storeNumber,
          page,
          pageSize,
          facets: mergedFacets,
          priceRange,
        }),
      });

      if (response.ok) {
        return await response.json();
      } else {
        return { message: 'error', status: response.status };
      }
    }
  } catch (error) {
    console.error('Search error:', error);
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { message, status: 500 };
  }
}
